/**
 * FASE 3 v1.67 — Admin GDPR Requests Dashboard
 * Lista richieste GDPR (access/rectify/delete/restrict/export/oppose)
 * con azioni approve/reject/in_review.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle, XCircle, Clock, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/privacy`;
const axc = { withCredentials: true };

const STATUS_LABELS = {
  pending: { label: "In attesa", color: "bg-amber-100 text-amber-800" },
  in_review: { label: "In esame", color: "bg-blue-100 text-blue-800" },
  completed: { label: "Completata", color: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rifiutata", color: "bg-rose-100 text-rose-800" },
};
const TYPE_LABELS = {
  access: "Accesso (art. 15)",
  rectify: "Rettifica (art. 16)",
  delete: "Cancellazione (art. 17)",
  restrict: "Limitazione (art. 18)",
  export: "Portabilità (art. 20)",
  oppose: "Opposizione (art. 21)",
};

export default function AdminGdprRequestsPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [modalReq, setModalReq] = useState(null);
  const [notes, setNotes] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/gdpr-requests?status=${status}`, axc);
      setItems(data?.items || []);
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail : "Errore caricamento richieste GDPR";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  const resolve = async (req, action) => {
    setBusy(req.id);
    try {
      const body = { action, notes: notes || null };
      if (action === "approve" && req.type === "export" && downloadUrl) {
        body.download_url = downloadUrl;
      }
      await axios.post(`${API}/gdpr-requests/${req.id}/resolve`, body, axc);
      toast.success(`Richiesta ${action === "approve" ? "approvata" : action === "reject" ? "rifiutata" : "in esame"}`);
      setModalReq(null); setNotes(""); setDownloadUrl("");
      await load();
    } catch (e) {
      const msg = typeof e?.response?.data?.detail === "string"
        ? e.response.data.detail : "Errore risoluzione richiesta";
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-gdpr-requests-page">
        <div className="mb-6 flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-emerald-700" />
          <div>
            <h1 className="text-2xl font-semibold text-[#0F1F14]">Richieste GDPR clienti</h1>
            <p className="text-sm text-zinc-500">
              Gestione self-service diritti art. 15-21 GDPR
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="gdpr-status-filter">
          {["pending", "in_review", "completed", "rejected", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`gdpr-filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                status === s ? "bg-emerald-700 text-white" : "bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-100"
              }`}
            >
              {s === "all" ? "Tutte" : STATUS_LABELS[s]?.label || s}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-zinc-500 text-center py-12">Caricamento...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-zinc-400 text-center py-12">Nessuna richiesta con questo stato.</p>
        ) : (
          <ul className="space-y-2" data-testid="gdpr-requests-list">
            {items.map((r) => (
              <li key={r.id}
                  data-testid={`gdpr-request-${r.id}`}
                  className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-[#163321]">{TYPE_LABELS[r.type] || r.type}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${STATUS_LABELS[r.status]?.color || "bg-zinc-100"}`}>
                      {STATUS_LABELS[r.status]?.label || r.status}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    <span className="font-medium">{r.client_email}</span>
                    {" · "}<Clock className="inline w-3 h-3" /> {String(r.requested_at || "").slice(0, 16).replace("T", " ")}
                  </p>
                  {r.admin_notes && (
                    <p className="text-[11px] text-zinc-600 mt-1 italic">Note: {r.admin_notes}</p>
                  )}
                  {r.download_url && (
                    <a href={r.download_url}
                       target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline mt-1"
                       data-testid={`gdpr-download-${r.id}`}>
                      <Download className="w-3 h-3" /> ZIP export
                    </a>
                  )}
                </div>
                {r.status === "pending" || r.status === "in_review" ? (
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => setModalReq({ ...r, _action: "approve" })}
                      disabled={busy === r.id}
                      data-testid={`gdpr-approve-${r.id}`}
                      className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <CheckCircle className="w-3 h-3 inline mr-1" /> Approva
                    </button>
                    <button
                      onClick={() => setModalReq({ ...r, _action: "in_review" })}
                      disabled={busy === r.id}
                      data-testid={`gdpr-review-${r.id}`}
                      className="px-3 py-1.5 bg-white border border-zinc-300 text-zinc-700 rounded-lg text-xs font-medium hover:bg-zinc-50 disabled:opacity-50"
                    >
                      In esame
                    </button>
                    <button
                      onClick={() => setModalReq({ ...r, _action: "reject" })}
                      disabled={busy === r.id}
                      data-testid={`gdpr-reject-${r.id}`}
                      className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-medium hover:bg-rose-700 disabled:opacity-50"
                    >
                      <XCircle className="w-3 h-3 inline mr-1" /> Rifiuta
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {/* Modal risoluzione */}
        {modalReq && (
          <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4"
               onClick={() => setModalReq(null)}
               data-testid="gdpr-resolve-modal">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5"
                 onClick={(e) => e.stopPropagation()}>
              <h4 className="text-base font-semibold text-[#163321] mb-3">
                {modalReq._action === "approve" ? "Approva richiesta" :
                 modalReq._action === "reject" ? "Rifiuta richiesta" : "Segna in esame"}
              </h4>
              <p className="text-xs text-zinc-600 mb-3">
                {TYPE_LABELS[modalReq.type]} · {modalReq.client_email}
              </p>
              {modalReq._action === "approve" && modalReq.type === "export" && (
                <>
                  <label className="text-xs text-zinc-600 font-medium">URL download ZIP export</label>
                  <input value={downloadUrl} onChange={(e) => setDownloadUrl(e.target.value)}
                         placeholder="https://..."
                         data-testid="gdpr-download-url-input"
                         className="w-full mt-1 mb-3 px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
                </>
              )}
              <label className="text-xs text-zinc-600 font-medium">Note (opzionale)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                         rows={3}
                         data-testid="gdpr-notes-input"
                         className="w-full mt-1 mb-4 px-3 py-2 border border-zinc-300 rounded-lg text-sm" />
              <div className="flex gap-2">
                <button onClick={() => resolve(modalReq, modalReq._action)}
                        data-testid="gdpr-resolve-submit"
                        className={`flex-1 py-2 text-white rounded-lg text-sm font-medium ${
                          modalReq._action === "reject" ? "bg-rose-600 hover:bg-rose-700"
                            : modalReq._action === "in_review" ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-emerald-700 hover:bg-emerald-800"
                        }`}>
                  Conferma
                </button>
                <button onClick={() => setModalReq(null)}
                        className="px-4 py-2 text-sm text-zinc-500 hover:text-zinc-800">
                  Annulla
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
